import { useState } from 'react';
import { Text, TwButton as Button, Loader } from '@ui-component-library';
import { updateUser } from '../../../../api/services/user';
import { validatePNR } from '../../../../api.js';
import { isValidPhoneNumber } from 'react-phone-number-input';
import FormSection from './FormSection';
import MuniSelectorDialog from './MuniSelectorDialog';
import { formatPersonalNumber, parsePersonalNumber } from './settingsHelpers';
import { formattedPhoneNumber } from '../../../login/helpers';

type ProfileSettingsProps = {
  userData: Record<string, unknown>;
  requiredFields: unknown;
  munis?: Array<{ label: string; value: string }>;
  onUserDataRefresh: () => void;
};

const FIELD_LABELS: Record<string, string> = {
  first_name: 'Förnamn',
  last_name: 'Efternamn',
  personal_number: 'Personnummer',
  mobile_phone_number: 'Mobilnummer',
  muni: 'Kommun',
  address: 'Adress',
  postal_code: 'Postnummer',
  city: 'Stad/Ort',
  company_name: 'Företagsnamn',
  organisation_number: 'Organisationsnummer',
  vat_number: 'VAT-nummer',
};

const fieldsToCheck = ['first_name', 'last_name', 'personal_number', 'mobile_phone_number', 'address', 'postal_code', 'city', 'company_name', 'organisation_number', 'vat_number'];

export default function ProfileSettings({ userData, requiredFields, munis, onUserDataRefresh }: ProfileSettingsProps) {
  const [formData, setFormData] = useState<Record<string, string | number | number[]>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<{ status: boolean, message: string }>({ status: false, message: '' });
  const [isMuniOpen, setIsMuniOpen] = useState(false);


  const isProfileDataReady =
    Object.keys(userData || {}).length > 0 && (userData as Record<string, unknown>)?.email !== undefined;

  const getValue = (field: string): string => {
    const val = formData[field] ?? (userData as Record<string, unknown>)?.[field];
    return val != null ? String(val) : '';
  };

  const handleChange = (field: string, val: string | number | number[]) => {
    if (field === 'muni') {
      setFormData((data) => ({ ...data, muni: val as number[] }));
    } else {
      const current = getValue(field);
      if (val !== current) {
        setFormData((data) => ({ ...data, [field]: val }));
      }
    }
  };

  const getSelectedMuniIds = (): number[] => {
    const munis = formData.muni ?? (userData.muni as Array<{ label: string; value: string }>);
    if (!munis || !Array.isArray(munis)) return [];
    return munis.map((item) => (typeof item === 'object' ? parseInt(String(item.value)) : Number(item)));
  };

  const handleMuniToggle = (muniId: number) => {
    const current = getSelectedMuniIds();
    const next = current.includes(muniId)
      ? current.filter((id) => id !== muniId)
      : [...current, muniId];
    setFormData((data) => ({ ...data, muni: next }));
  };


  const buildPayload = (): Record<string, string | number | number[]> => {
    const payload: Record<string, string | number | number[]> = {};
    for (const [key, value] of Object.entries(formData)) {
      if (value === '' || value === null || value === undefined) continue;
      if (key === 'muni' && Array.isArray(value) && value.length === 0) continue;
      if (typeof value === 'string' && !value.trim()) continue;
      payload[key] = value;
    }
    return payload;
  };

  const handleSubmit = async () => {
    let validationErrors: Record<string, string> = {};

    fieldsToCheck.forEach((field) => {
      const originalValue = (userData as Record<string, unknown>)[field];
      const hadValue = originalValue != null && String(originalValue).trim() !== '';
      const nowEmpty = !getValue(field).trim();
      if (hadValue && nowEmpty) {
        validationErrors[field] = `${FIELD_LABELS[field]}  är obligatoriskt.`;
      }
    });


    if (formData.personal_number && !validatePNR(String(formData.personal_number).replace(/-/g, ''))) {
      validationErrors.personal_number = 'Personnummer du angav är inte korrekt';
    }

    // Validate phone number
    if (formData.mobile_phone_number && !formattedPhoneNumber(String(formData.mobile_phone_number)) && !isValidPhoneNumber(String(formData.mobile_phone_number))) {
      validationErrors.mobile_phone_number = 'Telefonnumret du angav är inte korrekt';
    }
    else if (formData.mobile_phone_number) {
      formData.mobile_phone_number = formattedPhoneNumber(String(formData.mobile_phone_number)) || String(formData.mobile_phone_number);
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
    }
    else {
      const payload = buildPayload();
      if (Object.keys(payload).length > 0) {
        
        updateUser(formData).then((res) => {
          if (res.ok) {
            setSuccess(true);
            setFormData({})
            onUserDataRefresh?.();
            setError({})

          } else {
            if(res?.data?.messages){
              setErrors(res.data.messages)
              setError({})
            } else{
              setError({ status: true, message: 'Något gick fel. Försök igen.' });
            }
            setSuccess(false);
            //wait a moment to scroll to to allow error messages to show up so that scroll will "show" them on the way up
            setTimeout(() => {document.querySelector("#mainContainer").scrollTo({top:0, behavior: 'smooth'})}, 100)
          }
        });
      }
    }


  }

  if (!isProfileDataReady) {
    return (
      <div className="w-full max-w-[700px] mx-auto flex flex-col items-center justify-center py-16">
        <Loader size="large" />
        <Text variant="text-sm/leading-normal/normal" className="text-charcoal-400">
          Laddar...
        </Text>
      </div>
    );
  }



  return (
    <div className="w-full flex justify-center">
      <form onSubmit={(e) => { e.preventDefault(); handleSubmit() }} className="space-y-2 px-2 w-full max-w-[700px]">

        <FormSection title="Kontaktinformation">
          <FormSection.Grid>
            <FormSection.Field
              name="first_name"
              label="Förnamn"
              type="text"
              value={getValue('first_name')}
              error={errors.first_name}
              onChange={(val) => handleChange('first_name', val)}
              onClearError={() => setErrors((e) => ({ ...e, first_name: '' }))}
              show={!requiredFields || (Array.isArray(requiredFields) && requiredFields.includes('first_name'))}
            />
            <FormSection.Field
              name="last_name"
              label="Efternamn"
              type="text"
              value={getValue('last_name')}
              error={errors.last_name}
              onChange={(val) => handleChange('last_name', val)}
              onClearError={() => setErrors((e) => ({ ...e, last_name: '' }))}
              show={!requiredFields || (Array.isArray(requiredFields) && requiredFields.includes('last_name'))}
            />
          </FormSection.Grid>
          <FormSection.Grid>
            <FormSection.Field
              name="personal_number"
              label="Personnummer (ÅÅÅÅMMDD-XXXX)"
              type="identityNumber"
              value={formatPersonalNumber(getValue('personal_number'))}
              error={errors.personal_number}
              onChange={(val) => handleChange('personal_number', parsePersonalNumber(String(val)))}
              onClearError={() => setErrors((e) => ({ ...e, personal_number: '' }))}
              show={!requiredFields || (Array.isArray(requiredFields) && requiredFields.includes('personal_number'))}
            />
            <FormSection.Field
              name="mobile_phone_number"
              label="Mobilnummer"
              type="phone"
              value={getValue('mobile_phone_number')}
              error={errors.mobile_phone_number}
              onChange={(val) => handleChange('mobile_phone_number', val)}
              onClearError={() => setErrors((e) => ({ ...e, mobile_phone_number: '' }))}
              show={!requiredFields || (Array.isArray(requiredFields) && requiredFields.includes('mobile_phone_number'))}
            />
          </FormSection.Grid>
          {(userData as Record<string, unknown>)?.email && (
            <div>
              <label className="text-sm text-charcoal-400">E-postadress</label>
              <Text variant="text-sm/leading-normal/medium" className="text-charcoal-1000 mt-1">
                {(userData as Record<string, unknown>).email as string}
              </Text>
            </div>
          )}
        </FormSection>

        <FormSection title="Hemadress">
          <FormSection.Grid>
            <FormSection.Field
              name="address"
              label="Adress"
              type="text"
              value={getValue('address')}
              error={errors.address}
              onChange={(val) => handleChange('address', val)}
              onClearError={() => setErrors((e) => ({ ...e, address: '' }))}
              show={!requiredFields || (Array.isArray(requiredFields) && requiredFields.includes('address'))}
            />
            <FormSection.Field
              name="postal_code"
              label="Postnummer"
              type="number"
              value={getValue('postal_code')}
              error={errors.postal_code}
              onChange={(val) => handleChange('postal_code', val)}
              onClearError={() => setErrors((e) => ({ ...e, postal_code: '' }))}
              show={!requiredFields || (Array.isArray(requiredFields) && requiredFields.includes('postal_code'))}
            />
            <FormSection.Field
              name="city"
              label="Stad/Ort"
              type="text"
              value={getValue('city')}
              error={errors.city}
              onChange={(val) => handleChange('city', val)}
              onClearError={() => setErrors((e) => ({ ...e, city: '' }))}
              show={!requiredFields || (Array.isArray(requiredFields) && requiredFields.includes('city'))}
            />
          </FormSection.Grid>
        </FormSection>

        <section className="rounded-2xl bg-white-50 border-solid border-1 border-cloud-500 p-4 max-h-64 overflow-y-auto">
          <div className="flex justify-between items-center">
            <Text variant="text-base/leading-normal/medium" className="text-charcoal-1000 mb-4">
              Dina valda kommuner
            </Text>
            <Button variant="outline" size="medium" onClick={() => { setIsMuniOpen(true) }}>Lägg till kommun</Button>
          </div>
          <div className="space-y-2">
            {(!requiredFields ||
              (Array.isArray(requiredFields) && requiredFields.includes('muni'))) && (
                <div className="w-full">
                  {isMuniOpen && (
                    <MuniSelectorDialog
                      isOpen={isMuniOpen}
                      onClose={() => setIsMuniOpen(false)}
                      munis={munis ?? []}
                      selectedIds={getSelectedMuniIds()}
                      onToggle={handleMuniToggle}
                    />
                  )}
                  <div className="flex flex-wrap gap-2 mt-2">
                    {getSelectedMuniIds().map((id) => {
                      const muni = (munis ?? []).find((x) => parseInt(x.value) === id);
                      return muni ? (
                        <div
                          key={id}
                          className="flex w-full items-center justify-between gap-2 rounded-lg border border-cloud-500 bg-cloud-50 px-3 py-2"
                        >
                          <Text variant="text-sm/leading-normal/medium" className="text-charcoal-1000">
                            {muni.label}
                          </Text>
                          <Button
                            type="button"
                            variant="tertiary"
                            size="small"
                            onClick={() => handleMuniToggle(id)}

                          >
                            Ta bort
                          </Button>
                        </div>
                      ) : null;
                    })}
                  </div>
                </div>
              )}
          </div>
        </section>

        <FormSection title="För företag">
          <FormSection.Grid>
            <FormSection.Field
              name="company_name"
              label="Företagsnamn"
              type="text"
              value={getValue('company_name')}
              error={errors.company_name}
              onChange={(val) => handleChange('company_name', val)}
              onClearError={() => setErrors((e) => ({ ...e, company_name: '' }))}
              show={!requiredFields || (Array.isArray(requiredFields) && requiredFields.includes('company_name'))}
            />
            <FormSection.Field
              name="organisation_number"
              label="Organisationsnummer"
              type="text"
              value={getValue('organisation_number')}
              error={errors.organisation_number}
              onChange={(val) => handleChange('organisation_number', val)}
              onClearError={() => setErrors((e) => ({ ...e, organisation_number: '' }))}
              show={!requiredFields || (Array.isArray(requiredFields) && requiredFields.includes('organisation_number'))}
            />
            <FormSection.Field
              name="vat_number"
              label="Momsregistreringsnummer"
              type="text"
              value={getValue('vat_number')}
              error={errors.vat_number}
              onChange={(val) => handleChange('vat_number', val)}
              onClearError={() => setErrors((e) => ({ ...e, vat_number: '' }))}
              show={!requiredFields || (Array.isArray(requiredFields) && requiredFields.includes('vat_number'))}
            />
          </FormSection.Grid>
        </FormSection>

        {success && (
          <div className="rounded-lg bg-success-100 border border-success-300 px-4 py-3 text-sm text-success-800">
            Din information har sparats.
          </div>
        )}
        {error.status && (
          <div className="rounded-lg bg-danger-100 border border-danger-100 px-4 py-3 text-sm text-danger-500">
            {error.message}
          </div>
        )}

        <div className="flex justify-end pt-2">

          <Button
            type="submit"
            variant="primary"
            disabled={Object.keys(formData).length === 0}
          >
            Spara uppgifter
          </Button>
        </div>
        
      </form>
    </div>
  );
}
